import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonButton,
  IonContent,
  IonItem,
  IonLabel,
  IonList,
  IonReorder,
  IonReorderGroup,
  ItemReorderEventDetail,
  IonFooter,
  IonAlert,
  IonIcon,
  IonToast,
  IonText,
  IonItemSliding,
  IonItemOptions,
  IonItemOption,
} from "@ionic/react";
import React, { Fragment, memo, useContext, useEffect, useState } from "react";
import UserPreferencesContext, {
  UserPreferences,
} from "../../../components/contexts/UserPreferencesContext";
import { tradingGridGroupGetter } from "../../../utils/commonUtils";
import { saveUserPreferences } from "../../../utils/core-utils/preferenceUtils";
import {
  checkmarkCircleOutline,
  closeOutline,
  trashBinOutline,
} from "ionicons/icons";
import { send } from "../../../utils/sockets/asyncSocket";
import config from "../../../components/helpers/socket/config";
import { v4 as uuidv4 } from "uuid";

type EditGridsModalProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  currentSelectedProfile: number | undefined;
  tradingGridGroup: any;
  currentTradingGroup: any;
  setCurrentTradingGroup: (groupId: any) => void;
  selectedTradingGroupValue: React.MutableRefObject<any>;
  updateGridGroupPreference: () => void;
  unsubscribeTradingGridGroupItemMarketData: (groupId: any) => void;
};

interface ReorderEndCustomEvent extends CustomEvent {
  detail: ItemReorderEventDetail;
  target: HTMLIonReorderGroupElement;
}

const EditGridsModalComponent: React.FC<EditGridsModalProps> = ({
  isOpen,
  setIsOpen,
  currentSelectedProfile,
  tradingGridGroup,
  currentTradingGroup,
  setCurrentTradingGroup,
  selectedTradingGroupValue,
  updateGridGroupPreference,
  unsubscribeTradingGridGroupItemMarketData,
}) => {
  const { userPreferences, setUserPreferences } = useContext(
    UserPreferencesContext,
  );
  const [items, setItems] = useState<any[]>([]);
  const [removedGroupIds, setRemovedGroupIds] = useState<any[]>([]);
  const [isArrangeSaved, setIsArrangeSaved] = useState(false);
  const [hasUserArranged, setHasUserArranged] = useState(false);
  const [isCloseAlertOpen, setIsCloseAlertOpen] = useState(false);
  const [isApplyConfirmAlertOpen, setIsApplyConfirmAlertOpen] = useState(false);

  const currentGridGroups: any[] = JSON.parse(
    userPreferences?.tradinggridgroupsorder || "[]",
  );

  function handleReorderEnd(event: ReorderEndCustomEvent) {
    setItems(event.detail.complete(items));
    setHasUserArranged(true);
  }

  const handleRemoveGroup = (itemId: any) => {
    setItems((prevItems) => prevItems.filter((id) => id !== itemId));
    setRemovedGroupIds((prev) => [...prev, itemId]);
    setHasUserArranged(true);
  };

  const getRemovedGroupsNamesMarkup = () => {
    const names = removedGroupIds
      .map((id) => {
        const gridData = tradingGridGroupGetter(tradingGridGroup, id);
        return gridData ? `• ${gridData.n}` : "";
      })
      .filter(Boolean)
      .join("\n");
    return `Are you sure you want to remove these groups?\n\n${names}`;
  };

  const executeApplyChanges = () => {
    const idsToToggle = [...removedGroupIds];

    const targetObject = currentGridGroups.find(
      (item: any) => item?.p === currentSelectedProfile,
    );

    if (targetObject) {
      targetObject.g = items;
    }

    const updatedPreferences: UserPreferences = {
      ...userPreferences,
      tradinggridgroupsorder: JSON.stringify(currentGridGroups),
    };

    const isActiveGridRemoved =
      currentTradingGroup && idsToToggle.includes(currentTradingGroup);

    if (isActiveGridRemoved) {
      const newFocusedGroup = items.length > 0 ? items[0] : undefined;

      unsubscribeTradingGridGroupItemMarketData(currentTradingGroup);

      setCurrentTradingGroup(newFocusedGroup);
      selectedTradingGroupValue.current = newFocusedGroup;
    }

    setUserPreferences(updatedPreferences);

    saveUserPreferences(updatedPreferences);

    if (idsToToggle.length > 0) {
      updateGridGroupPreference();
    }

    idsToToggle.forEach((id) => {
      send(config.socket.channels.TRADING, {
        action: config.socket.actions.CMED_TOGGLE_GROUP_VISIBILITY,
        requestID: uuidv4(),
        type: "toggle",
        payload: {
          groupId: String(id),
          visible: "False",
        },
      });
    });

    setIsOpen(false);
    setIsArrangeSaved(true);
  };

  const handleApplyChanges = () => {
    if (removedGroupIds.length > 0) {
      setIsApplyConfirmAlertOpen(true);
    } else {
      executeApplyChanges();
    }
  };

  const handleClose = () => {
    if (hasUserArranged) {
      setIsCloseAlertOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setItems(
        JSON.parse(userPreferences?.tradinggridgroupsorder || "[]").find(
          (item: any) => item?.p === currentSelectedProfile,
        )?.g,
      );
      setRemovedGroupIds([]);
    } else {
      setItems([]);
      setHasUserArranged(false);
      setRemovedGroupIds([]);
    }
  }, [isOpen, userPreferences?.tradinggridgroupsorder]);

  return (
    <Fragment>
      <style>{`
        .pre-wrap-alert, .pre-wrap-alert .alert-message {
          white-space: pre-wrap !important;
        }
      `}</style>
      <IonModal isOpen={isOpen}>
        <IonHeader className="ion-no-border">
          <IonToolbar>
            <IonTitle>
              <p className="ion-no-margin text-bold">EDIT MARKET</p>
            </IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={handleClose}>
                <IonIcon
                  icon={closeOutline}
                  color="dark"
                  slot="icon-only"
                ></IonIcon>
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <IonText>
            <h6
              className="ion-no-margin"
              style={{ fontWeight: "bold", marginBottom: "10px" }}
            >
              Arrange Tabs
            </h6>
          </IonText>
          <IonList>
            <IonReorderGroup
              disabled={false}
              onIonItemReorder={handleReorderEnd}
            >
              {items?.map((item: any) => {
                const gridData = tradingGridGroupGetter(tradingGridGroup, item);
                if (!gridData) return null;
                return (
                  <IonItemSliding key={item}>
                    <IonItem>
                      <IonLabel>{gridData.n}</IonLabel>
                      <IonReorder slot="end"></IonReorder>
                    </IonItem>
                    <IonItemOptions side="end">
                      <IonItemOption
                        color="danger"
                        onClick={() => handleRemoveGroup(item)}
                      >
                        <IonIcon
                          icon={trashBinOutline}
                          slot="icon-only"
                        ></IonIcon>
                      </IonItemOption>
                    </IonItemOptions>
                  </IonItemSliding>
                );
              })}
            </IonReorderGroup>
          </IonList>
        </IonContent>
        <IonFooter className="ion-no-border ion-padding">
          <IonButton
            onClick={handleApplyChanges}
            expand="block"
            className="custom_cmed_mobile_button"
            disabled={!hasUserArranged}
          >
            APPLY CHANGES
          </IonButton>
        </IonFooter>
      </IonModal>
      <IonAlert
        isOpen={isCloseAlertOpen}
        header={"Discard Changes?"}
        message={
          "The changes will be lost if the screen is closed without applying."
        }
        buttons={[
          {
            text: "Cancel",
            role: "cancel",
            handler: () => setIsCloseAlertOpen(false),
          },
          {
            text: "Discard",
            handler: () => setIsOpen(false),
          },
        ]}
        onDidDismiss={() => setIsCloseAlertOpen(false)}
      ></IonAlert>
      <IonAlert
        isOpen={isApplyConfirmAlertOpen}
        header={"Remove Groups"}
        cssClass="pre-wrap-alert one-alert"
        message={getRemovedGroupsNamesMarkup()}
        buttons={[
          {
            text: "No",
            role: "cancel",
            handler: () => {
              setItems((prev) => [...prev, ...removedGroupIds]);
              setRemovedGroupIds([]);
              setIsApplyConfirmAlertOpen(false);
            },
          },
          {
            text: "Yes",
            handler: () => {
              setIsApplyConfirmAlertOpen(false);
              executeApplyChanges();
            },
          },
        ]}
        onDidDismiss={() => setIsApplyConfirmAlertOpen(false)}
></IonAlert>
      <IonToast
        isOpen={isArrangeSaved}
        message="New arrangement is set."
        onDidDismiss={() => setIsArrangeSaved(false)}
        duration={5000}
        color="success"
        icon={checkmarkCircleOutline}
        buttons={[
          {
            text: "Dismiss",
            role: "cancel",
          },
        ]}
      ></IonToast>
    </Fragment>
  );
};

export const EditGridsModal = memo(EditGridsModalComponent);
